import React, { useState, useEffect, useCallback } from "react";
import { Search, Download, Printer, Eye, Trash2, ArrowLeft, Pencil, FileSpreadsheet, FileText, Wine, CreditCard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useBranding } from "./BrandingContext";
import { useAuth } from "./AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ onBack, onGoBar }) {
  const branding = useBranding();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [total, setTotal] = useState(0);
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(Date.now());
  const [exporting, setExporting] = useState(false);

  const INITIAL_LIMIT = 15;
  const LOAD_MORE_LIMIT = 50;

  const fetchMembers = useCallback(async (searchTerm = "") => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/members`, {
        // cache-bust so edits/regens are immediately reflected
        params: { search: searchTerm, skip: 0, limit: INITIAL_LIMIT, _ts: Date.now() },
      });
      setMembers(response.data.members || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const response = await axios.get(`${API}/members`, {
        params: { search, skip: members.length, limit: LOAD_MORE_LIMIT, _ts: Date.now() },
      });
      const next = response.data.members || [];
      // De-dupe just in case a new member was added between calls
      setMembers((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        return [...prev, ...next.filter((m) => !seen.has(m.id))];
      });
      setTotal(response.data.total || 0);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      toast.error("Failed to load more members");
    } finally {
      setLoadingMore(false);
    }
  }, [members.length, search]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMembers(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search, fetchMembers]);

  const handleDownload = async (memberId) => {
    try {
      const response = await axios.get(`${API}/download-pdf/${memberId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `member_${memberId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded!");
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      toast.error("Failed to download PDF");
    }
  };

  const handleDownloadAadhar = async (memberId, memberName) => {
    try {
      const response = await axios.get(`${API}/download-aadhar/${memberId}`, { responseType: 'blob' });
      let filename = `aadhar_${(memberName || 'member').replace(/\s+/g, '_')}.jpg`;
      const cd = response.headers && response.headers['content-disposition'];
      if (cd) {
        const m = /filename="?([^"]+)"?/.exec(cd);
        if (m) filename = m[1];
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Aadhar downloaded!");
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      toast.error("Failed to download Aadhar");
    }
  };

  const handleDownloadCard = async (memberId, memberNumber) => {
    try {
      const response = await axios.get(`${API}/download-card/${memberId}?t=${Date.now()}`, { responseType: 'blob' });
      const safeNum = (memberNumber || 'member').replace(/\W+/g, '_');
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'image/png' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `id_card_${safeNum}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("ID Card image downloaded!");
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      toast.error("Failed to download ID Card");
    }
  };

  const handlePrint = (memberId) => {
    const printUrl = `${API}/preview-form/${memberId}?t=${Date.now()}`;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const doc = printWindow.document;
    doc.title = "Member Form";
    const style = doc.createElement('style');
    style.textContent = "body{margin:0;display:flex;justify-content:center;align-items:center}img{max-width:100%;height:auto}@media print{body{margin:0}img{max-width:100%}}";
    doc.head.appendChild(style);
    const img = doc.createElement('img');
    img.onload = () => { printWindow.print(); printWindow.close(); };
    img.src = printUrl;
    doc.body.appendChild(img);
  };

  const handleDelete = async (memberId, memberName) => {
    if (!window.confirm(`Delete member "${memberName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/member/${memberId}`);
      toast.success("Member deleted");
      fetchMembers(search);
      if (selectedMember?.id === memberId) setSelectedMember(null);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      toast.error("Failed to delete member");
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setEditForm({
      member_number: member.member_number || "",
      name: member.name || "",
      father_name: member.father_name || "",
      dob: member.dob || "",
      age: member.age || "",
      aadhar_number: member.aadhar_number || "",
      mobile: member.mobile || "",
      email: member.email || "",
      address: member.address || "",
      occupation: member.occupation || "",
      joining_date: member.joining_date || "",
    });
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleSaveEdit = async () => {
    if (!editingMember) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/member/${editingMember.id}`, editForm);
      toast.success("Member updated");
      setEditingMember(null);
      setPreviewKey(Date.now());
      if (selectedMember?.id === editingMember.id) {
        setSelectedMember(data.member);
      }
      fetchMembers(search);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      toast.error("Failed to update member");
    } finally {
      setSaving(false);
    }
  };

  const [pdfJob, setPdfJob] = useState(null); // {jobId, done, total, status}

  const handleConsolidatedPdf = async () => {
    try {
      const { data } = await axios.post(`${API}/consolidated-pdf/start`);
      const jobId = data.job_id;
      setPdfJob({ jobId, done: 0, total: 0, status: "running" });
      let fails = 0;
      const poll = setInterval(async () => {
        try {
          const { data: s } = await axios.get(`${API}/consolidated-pdf/status/${jobId}`);
          fails = 0;
          if (s.status === "running") {
            setPdfJob({ jobId, done: s.done, total: s.total, status: "running" });
          } else if (s.status === "ready") {
            clearInterval(poll);
            setPdfJob(null);
            const resp = await axios.get(`${API}/consolidated-pdf/download/${jobId}?t=${Date.now()}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "all_members_form_aadhar_idcard.pdf");
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Consolidated PDF downloaded!");
          } else {
            clearInterval(poll);
            setPdfJob(null);
            toast.error(s.error || "PDF build failed — click again to resume (progress is saved)", { duration: 8000 });
          }
        } catch (e) {
          fails += 1;
          if (fails >= 8) {
            clearInterval(poll);
            setPdfJob(null);
            toast.error("Connection lost while building. Click the button again — progress is saved and it will resume quickly.", { duration: 8000 });
          }
        }
      }, 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start PDF build");
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/members/export.csv`, {
        responseType: "blob",
        // cache-bust so latest edits show up in the downloaded CSV
        params: { _ts: Date.now() },
        headers: { "Cache-Control": "no-cache" },
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `members_report_${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Members report downloaded");
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      toast.error("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-slate-900 border-b border-amber-700/30 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="rounded-full text-amber-300 hover:text-amber-400 hover:bg-amber-900/30"
            data-testid="dashboard-back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold tracking-[0.18em] text-amber-400 truncate" data-testid="dashboard-header-title">{branding.club_name}</h1>
            <p className="text-xs text-amber-200/70">Members · {total} {total === 1 ? 'member' : 'members'}</p>
          </div>
          {onGoBar && isAdmin && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onGoBar}
              className="rounded-xl text-amber-300 hover:text-amber-400 hover:bg-amber-900/30 flex-shrink-0"
              data-testid="dashboard-bar-link"
              title="Bar Module"
            >
              <Wine className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Search + Export */}
      <div className="max-w-md mx-auto px-6 py-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, mobile, Aadhar, or member ID..."
            className="pl-11 rounded-xl bg-white border-slate-200 h-12"
            data-testid="member-search-input"
          />
        </div>
        <Button
          onClick={handleExportCsv}
          disabled={exporting || members.length === 0}
          variant="outline"
          className="w-full h-11 rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50 font-semibold"
          data-testid="export-csv-button"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          {exporting ? "Exporting..." : "Download Consolidated Report (CSV)"}
        </Button>
        {isAdmin && (
          <Button
            onClick={handleConsolidatedPdf}
            disabled={!!pdfJob || members.length === 0}
            variant="outline"
            className="w-full h-11 rounded-xl border-indigo-300 text-indigo-700 hover:bg-indigo-50 font-semibold"
            data-testid="consolidated-pdf-button"
          >
            <FileText className="w-4 h-4 mr-2" />
            {pdfJob
              ? `Building PDF... ${pdfJob.done}/${pdfJob.total || "?"} members`
              : "Download All Members PDF (Form + Aadhar + ID Card)"}
          </Button>
        )}
      </div>

      {/* Members list */}
      <div className="max-w-md mx-auto px-6 space-y-3">
        {loading && (
          <div className="text-center text-slate-500 py-8">Loading...</div>
        )}
        
        {!loading && members.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">
              {search ? "No members found matching your search." : "No members yet. Create your first member!"}
            </p>
          </div>
        )}
        
        {!loading && members.map((member, index) => (
          <motion.div
            key={member.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04, duration: 0.2 }}
            className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm hover:shadow-md transition-shadow"
            data-testid={`member-card-${member.id}`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <span className="text-lg font-bold text-amber-700">{(member.name || "?").charAt(0).toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-semibold text-slate-900 truncate">{member.name}</h3>
                  {member.member_number && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 tracking-wider" data-testid={`member-id-badge-${member.id}`}>
                      {member.member_number}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">Aadhar: {member.aadhar_number}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                  {member.mobile && <span>📱 {member.mobile}</span>}
                  <span>Joined: {member.joining_date}</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between gap-1.5 mt-3 pt-3 border-t border-slate-100" data-testid={`member-actions-${member.id}`}>
              <Button
                size="icon"
                variant="outline"
                onClick={() => setSelectedMember(member)}
                className="h-9 w-9 rounded-lg"
                title="View"
                aria-label="View"
                data-testid={`view-member-${member.id}`}
              >
                <Eye className="w-4 h-4" />
              </Button>
              {isAdmin && (
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => handleEdit(member)}
                  className="h-9 w-9 rounded-lg border-blue-200 text-blue-600 hover:bg-blue-50"
                  title="Edit"
                  aria-label="Edit"
                  data-testid={`edit-member-${member.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              <Button
                size="icon"
                variant="outline"
                onClick={() => handleDownload(member.id)}
                className="h-9 w-9 rounded-lg border-amber-200 text-amber-600 hover:bg-amber-50"
                title="Download PDF"
                aria-label="Download PDF"
                data-testid={`download-member-${member.id}`}
              >
                <Download className="w-4 h-4" />
              </Button>
              {isAdmin && (
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => handleDownloadAadhar(member.id, member.name)}
                  className="h-9 w-9 rounded-lg border-green-200 text-green-700 hover:bg-green-50"
                  title="Download Aadhar (admin only)"
                  aria-label="Download Aadhar"
                  data-testid={`download-aadhar-${member.id}`}
                >
                  <FileText className="w-4 h-4" />
                </Button>
              )}
              <Button
                size="icon"
                variant="outline"
                onClick={() => handleDownloadCard(member.id, member.member_number)}
                className="h-9 w-9 rounded-lg border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                title="Download ID Card (Image)"
                aria-label="Download ID Card"
                data-testid={`download-card-${member.id}`}
              >
                <CreditCard className="w-4 h-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => handlePrint(member.id)}
                className="h-9 w-9 rounded-lg"
                title="Print"
                aria-label="Print"
                data-testid={`print-member-${member.id}`}
              >
                <Printer className="w-4 h-4" />
              </Button>
              {isAdmin && (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleDelete(member.id, member.name)}
                  className="h-9 w-9 rounded-lg text-red-500 hover:bg-red-50"
                  title="Delete"
                  aria-label="Delete"
                  data-testid={`delete-member-${member.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </motion.div>
        ))}

        {!loading && members.length > 0 && members.length < total && (
          <div className="pt-2 pb-4">
            <Button
              onClick={loadMore}
              disabled={loadingMore}
              variant="outline"
              className="w-full h-11 rounded-xl border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold"
              data-testid="load-more-members-button"
            >
              {loadingMore
                ? "Loading..."
                : `Load More (${Math.min(LOAD_MORE_LIMIT, total - members.length)} of ${total - members.length} remaining)`}
            </Button>
          </div>
        )}

        {!loading && members.length > 0 && members.length >= total && total > INITIAL_LIMIT && (
          <p className="text-center text-xs text-slate-400 pt-2 pb-4" data-testid="load-more-end-marker">
            — End of list ({total} members) —
          </p>
        )}
      </div>

      {/* Modal for viewing member form */}
      <AnimatePresence>
        {selectedMember && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedMember(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="member-detail-modal"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{selectedMember.name}</h3>
                    <p className="text-sm text-slate-500">Joined: {selectedMember.joining_date}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedMember(null)} className="rounded-full">
                    ✕
                  </Button>
                </div>
                <div className="rounded-xl overflow-hidden border border-slate-200 mb-4">
                  <img
                    src={`${API}/preview-form/${selectedMember.id}?t=${previewKey}`}
                    alt="Member form"
                    className="w-full h-auto"
                    data-testid="preview-form-image"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleEdit(selectedMember)}
                    variant="outline"
                    className="flex-1 h-11 rounded-xl border-blue-200 text-blue-600 hover:bg-blue-50"
                    data-testid="modal-edit-button"
                  >
                    <Pencil className="w-4 h-4 mr-2" /> Edit
                  </Button>
                  <Button
                    onClick={() => handleDownload(selectedMember.id)}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white h-11 rounded-xl"
                  >
                    <Download className="w-4 h-4 mr-2" /> Download PDF
                  </Button>
                  <Button
                    onClick={() => handlePrint(selectedMember.id)}
                    variant="outline"
                    className="flex-1 h-11 rounded-xl border-amber-200 text-amber-600 hover:bg-amber-50"
                  >
                    <Printer className="w-4 h-4 mr-2" /> Print
                  </Button>
                </div>
                <Button
                  onClick={() => handleDownloadCard(selectedMember.id, selectedMember.member_number)}
                  className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700 text-white h-11 rounded-xl"
                  data-testid="modal-download-card-button"
                >
                  <CreditCard className="w-4 h-4 mr-2" /> Download ID Card (Image)
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* EDIT MODAL */}
      <AnimatePresence>
        {editingMember && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => !saving && setEditingMember(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="member-edit-modal"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Edit Member</h3>
                    <p className="text-xs text-slate-500">Form & ID card will update automatically</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setEditingMember(null)} className="rounded-full" disabled={saving}>✕</Button>
                </div>
                <div className="space-y-3">
                  {[
                    { name: "member_number", label: "Member ID", placeholder: "e.g. 0162 or M-2026-001" },
                    { name: "name", label: "Name (Tamil)" },
                    { name: "father_name", label: "Father / Husband Name (Tamil)" },
                    { name: "dob", label: "Date of Birth", placeholder: "DD/MM/YYYY" },
                    { name: "age", label: "Age" },
                    { name: "aadhar_number", label: "Aadhar Number" },
                    { name: "mobile", label: "Mobile" },
                    { name: "email", label: "Email" },
                    { name: "address", label: "Address (Tamil)", multiline: true },
                    { name: "occupation", label: "Occupation" },
                    { name: "joining_date", label: "Joining Date", placeholder: "DD/MM/YYYY" },
                  ].map((f) => (
                    <div key={f.name}>
                      <Label htmlFor={`edit-${f.name}`} className="text-xs font-semibold text-slate-700">{f.label}</Label>
                      {f.multiline ? (
                        <textarea
                          id={`edit-${f.name}`}
                          name={f.name}
                          value={editForm[f.name] || ""}
                          onChange={handleEditChange}
                          rows={2}
                          className="mt-1 w-full px-3 py-2 rounded-lg bg-slate-50 focus:bg-white border border-slate-200 focus:border-amber-500 focus:outline-none text-sm"
                          data-testid={`edit-input-${f.name}`}
                        />
                      ) : (
                        <Input
                          id={`edit-${f.name}`}
                          name={f.name}
                          value={editForm[f.name] || ""}
                          onChange={handleEditChange}
                          placeholder={f.placeholder || ""}
                          className="mt-1 rounded-lg bg-slate-50 focus:bg-white h-10"
                          data-testid={`edit-input-${f.name}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-6">
                  <Button
                    onClick={() => setEditingMember(null)}
                    variant="outline"
                    className="flex-1 h-11 rounded-xl"
                    disabled={saving}
                    data-testid="edit-cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white h-11 rounded-xl"
                    disabled={saving}
                    data-testid="edit-save-button"
                  >
                    {saving ? "Saving..." : "Save & Regenerate"}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
